import type { Campaign, Targeting, Variant } from '@dashboard/types/config'
import { targetingSchema } from '@dashboard/types/config'
import {
  ORIGINAL_ID,
  ORIGINAL_NAME,
  addVersion,
  clearFieldDelta,
  createTest,
  effectiveActions,
  endTest,
  liveTestState,
  removeVersion,
  renameVersion,
  setFieldDelta,
  setWeights,
  skipAction,
  undeploy,
  unskipAction
} from '@shared/campaignTests/audienceTest'
import type { Json, VersionId, VersionWeights } from '@shared/campaignTests/audienceTest'
import { classTokens } from '@shared/classTokens'
import { isActionKey } from '@shared/editor/model'
import type { ActionKey, TargetDescriptor } from '@shared/editor/model'
import { klona } from 'klona'
import { reorderMinimal } from './order'

export type StoredCampaign = Campaign
export type StoredAction = Variant['actions'][number]
export type StoredPageRule = StoredAction['page'][number]
export type SegmentRef = { groupId: string; segmentId: string; name?: string }
export type ChangePatch =
  | { kind: 'text'; value: string | null }
  | { kind: 'find-replace'; pairs: { find: string; replace: string }[] }
  | { kind: 'visibility'; value: 'show' | 'hide' | null }
  | { kind: 'style'; name: string; value: string | null }
  | { kind: 'attribute'; name: string; value: string | null }
  | { kind: 'classes'; add: string[]; remove: string[] }
  | { kind: 'pages'; value: StoredPageRule[] }
  | { kind: 'target'; target: TargetDescriptor }
export type CampaignEdit =
  | {
      kind: 'upsert'
      audienceId: string
      versionId?: VersionId
      target: TargetDescriptor
      actionKey?: ActionKey
      patch: ChangePatch
      page: StoredPageRule
      pages?: StoredPageRule[]
    }
  | { kind: 'remove'; actionKey: ActionKey; versionId?: VersionId }
  | { kind: 'same-as-a'; actionKey: ActionKey; versionId?: VersionId; path?: string[] }
  | { kind: 'unskip'; actionKey: ActionKey; versionId?: VersionId }
  | { kind: 'create-test'; audienceId: string }
  | { kind: 'add-version'; audienceId: string }
  | { kind: 'remove-version'; audienceId: string; versionId: VersionId }
  | { kind: 'remove-test'; audienceId: string }
  | { kind: 'rename-version'; audienceId: string; versionId: VersionId; name: string }
  | { kind: 'set-weights'; audienceId: string; weights: VersionWeights }
  | { kind: 'rename'; audienceId: string; name: string }
  | { kind: 'duplicate'; audienceId: string; name?: string }
  | { kind: 'delete'; audienceId: string }
  | { kind: 'targeting'; audienceId: string; operator: 'all' | 'any'; terms: [SegmentRef, ...SegmentRef[]] }
  | { kind: 'add-audiences'; terms: [SegmentRef, ...SegmentRef[]]; grouping: 'separate' | 'any' }
  | { kind: 'order'; highestFirst: [string, ...string[]] }
  | { kind: 'batch'; edits: CampaignEdit[] }
  | { kind: 'undeploy' }
export type IdAllocator = { variant(): string; action(): string }
export type EditError = { code: 'unknown-audience' | 'unknown-action' | 'empty-targeting' | 'invalid'; message: string }
export type EditOutcome =
  | { campaign: StoredCampaign; changed: ActionKey[]; created: { audienceIds: string[]; actionKey?: ActionKey } }
  | { error: EditError }
export type ActionEntry = { key: ActionKey; audience: Variant; action: StoredAction; index: number }

/** The original page as an editable node while it has a test: no targeting, no Control changes, only variants. */
export function originalAudience(campaign: Pick<StoredCampaign, 'original'> | undefined): Variant | undefined {
  const test = campaign?.original?.test
  return test ? { id: ORIGINAL_ID, name: ORIGINAL_NAME, rules: {}, actions: [], test } : undefined
}

function liveNode(liveCampaign: StoredCampaign | undefined, id: string): Variant | undefined {
  return id === ORIGINAL_ID ? originalAudience(liveCampaign) : liveCampaign?.variants.find((item) => item.id === id)
}

export function campaignActions(campaign: StoredCampaign, versionId: VersionId = 'A'): ActionEntry[] {
  const original = originalAudience(campaign)
  return (original ? [original, ...campaign.variants] : campaign.variants).flatMap((audience) => {
    const occurrences = new Map<string, number>()
    const actions =
      !audience.test || versionId === 'A'
        ? audience.actions
        : audience.test.versions.some((version) => version.id === versionId)
          ? effectiveActions(audience, versionId)
          : []
    return actions.flatMap((action, index) => {
      const occurrence = occurrences.get(action.id) ?? 0
      occurrences.set(action.id, occurrence + 1)
      const key = `${audience.id}:${action.id}:${occurrence}`
      return isActionKey(key) ? [{ key, audience, action, index }] : []
    })
  })
}

/** What the original layer renders for a version: a test variant's changes, or a deployed winner. */
export function originalLayerEntries(campaign: StoredCampaign, versionId: VersionId = 'A'): ActionEntry[] {
  if (campaign.original?.test) {
    return versionId === 'A'
      ? []
      : campaignActions(campaign, versionId).filter((entry) => entry.audience.id === ORIGINAL_ID)
  }
  const actions = campaign.original?.deployment?.actions ?? []
  const audience: Variant = { id: ORIGINAL_ID, name: ORIGINAL_NAME, rules: {}, actions }
  return actions.flatMap((action, index) => {
    const key = `${ORIGINAL_ID}:${action.id}:${index}`
    return isActionKey(key) ? [{ key, audience, action, index }] : []
  })
}

export function audienceTerms(audience: Variant): { operator: 'all' | 'any'; terms: SegmentRef[] } {
  const operator = audience.rules?.all?.length ? 'all' : audience.rules?.any?.length ? 'any' : 'all'
  return {
    operator,
    terms: (audience.rules?.[operator] ?? []).map((term) => ({ groupId: term.dimensionId, segmentId: term.segment }))
  }
}

function error(code: EditError['code'], message: string): EditOutcome {
  return { error: { code, message } }
}
function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? Object.fromEntries(Object.entries(value)) : {}
}
function validTargeting(value: Record<string, unknown>): value is Targeting {
  return targetingSchema.safeParse(value).success
}
function withTarget(action: StoredAction, target: TargetDescriptor): StoredAction | null {
  if (!target.selector.trim() || (target.targeting && !validTargeting(target.targeting))) {
    return null
  }
  const updated = { ...action, selector: target.selector }
  if (target.targeting && validTargeting(target.targeting)) {
    updated.targeting = target.targeting
  } else {
    delete updated.targeting
  }
  return updated
}
function patchAction(action: StoredAction, patch: ChangePatch): StoredAction | null {
  if (patch.kind === 'target') {
    return withTarget(action, patch.target)
  }
  if (patch.kind === 'pages') {
    return { ...action, page: patch.value }
  }
  const modifications = { ...action.modifications }
  switch (patch.kind) {
    case 'text':
      delete modifications.findReplace
      if (patch.value?.trim()) {
        modifications.text = patch.value
      } else {
        delete modifications.text
      }
      break
    case 'find-replace': {
      delete modifications.text
      const pairs = patch.pairs.filter((pair) => pair.find.length > 0)
      if (pairs.length) {
        modifications.findReplace = pairs
      } else {
        delete modifications.findReplace
      }
      break
    }
    case 'visibility':
      if (patch.value === null) {
        delete modifications.visibility
      } else {
        modifications.visibility = patch.value
      }
      break
    case 'style':
    case 'attribute': {
      const field = patch.kind === 'style' ? 'style' : 'attr'
      const values = record(modifications[field])
      if (patch.value === null || (patch.kind === 'style' && !patch.value.trim())) {
        delete values[patch.name]
      } else {
        values[patch.name] = patch.value
      }
      if (Object.keys(values).length) {
        modifications[field] = values
      } else {
        delete modifications[field]
      }
      break
    }
    case 'classes': {
      const classes = record(modifications.classes)
      const add = classTokens(patch.add)
      const remove = classTokens(patch.remove)
      delete classes.add
      delete classes.remove
      if (add.length) {
        classes.add = add
      }
      if (remove.length) {
        classes.remove = remove
      }
      if (Object.keys(classes).length) {
        modifications.classes = classes
      } else {
        delete modifications.classes
      }
      break
    }
  }
  return { ...action, modifications }
}
function meaningful(action: StoredAction): boolean {
  return Object.entries(action.modifications ?? {}).some(([name, value]) => {
    if (value === null || value === undefined) {
      return false
    }
    if (name === 'text') {
      return typeof value === 'string' && Boolean(value.trim())
    }
    if (name === 'findReplace') {
      return Array.isArray(value) && value.length > 0
    }
    if (name === 'style') {
      return Object.values(record(value)).some((item) => typeof item !== 'string' || Boolean(item.trim()))
    }
    if (name === 'attr') {
      return Object.keys(record(value)).length > 0
    }
    if (name === 'classes') {
      return Object.entries(record(value)).some(
        ([field, items]) => (field !== 'add' && field !== 'remove') || (Array.isArray(items) && items.length > 0)
      )
    }
    return true
  })
}
function replaceAudience(campaign: StoredCampaign, audience: Variant): StoredCampaign {
  if (audience.id === ORIGINAL_ID) {
    if (audience.actions.length) {
      throw new Error('Control is the original page. Change a variant or an audience instead.')
    }
    return { ...campaign, original: { ...campaign.original, test: audience.test } }
  }
  return { ...campaign, variants: campaign.variants.map((item) => (item.id === audience.id ? audience : item)) }
}
function uniqueTerms(terms: SegmentRef[]): SegmentRef[] {
  return terms.filter(
    (term, index) =>
      terms.findIndex((other) => other.groupId === term.groupId && other.segmentId === term.segmentId) === index
  )
}

function replaceActions(audience: Variant, versionId: VersionId, actions: StoredAction[]): Variant {
  if (versionId === 'A') {
    return { ...audience, actions }
  }
  const test = audience.test
  if (!test) {
    throw new Error('This audience has no test.')
  }
  const [baseline, ...challengers] = test.versions
  if (!challengers.some((version) => version.id === versionId)) {
    throw new Error(`Unknown version ${versionId}.`)
  }
  return {
    ...audience,
    test: {
      ...test,
      versions: [
        baseline,
        ...challengers.map((version) => (version.id === versionId ? { ...version, actions } : version))
      ]
    }
  }
}

function removeAction(campaign: StoredCampaign, entry: ActionEntry, versionId: VersionId): StoredCampaign {
  if (entry.audience.test || versionId !== 'A') {
    return skipAction(campaign, entry.audience.id, versionId, entry.action.id)
  }
  return replaceAudience(campaign, {
    ...entry.audience,
    actions: entry.audience.actions.filter((_, index) => index !== entry.index)
  })
}

function patchPaths(patch: ChangePatch): string[][] {
  switch (patch.kind) {
    case 'text':
    case 'find-replace':
      return [['text'], ['findReplace']]
    case 'style':
      return [['style', patch.name]]
    case 'attribute':
      return [['attr', patch.name]]
    case 'classes':
      return [
        ['classes', 'add'],
        ['classes', 'remove']
      ]
    case 'visibility':
      return [['visibility']]
    case 'pages':
    case 'target':
      return []
  }
}

function fieldValue(action: StoredAction, path: string[]): unknown {
  let value: unknown = action.modifications
  for (const key of path) {
    value = record(value)[key]
  }
  return value
}

export function applyCampaignEdit(
  campaign: StoredCampaign,
  edit: CampaignEdit,
  ids: IdAllocator,
  liveCampaign?: StoredCampaign
): EditOutcome {
  try {
    return applyEdit(campaign, edit, ids, liveCampaign)
  } catch (cause) {
    return error('invalid', cause instanceof Error ? cause.message : 'This change could not be applied.')
  }
}

function applyEdit(
  campaign: StoredCampaign,
  edit: CampaignEdit,
  ids: IdAllocator,
  liveCampaign?: StoredCampaign
): EditOutcome {
  const success = (
    next = campaign,
    changed: ActionKey[] = [],
    created?: { audienceIds: string[]; actionKey?: ActionKey }
  ): EditOutcome => ({ campaign: next, changed, created: created ?? { audienceIds: [] } })
  if (edit.kind === 'undeploy') {
    return success(undeploy(campaign))
  }
  if (edit.kind === 'batch') {
    let next = campaign
    const changed: ActionKey[] = []
    const created: { audienceIds: string[]; actionKey?: ActionKey } = { audienceIds: [] }
    for (const child of edit.edits) {
      const outcome = applyCampaignEdit(next, child, ids, liveCampaign)
      if ('error' in outcome) {
        return outcome
      }
      next = outcome.campaign
      changed.push(...outcome.changed)
      created.audienceIds.push(...outcome.created.audienceIds)
      if (outcome.created.actionKey) {
        created.actionKey = outcome.created.actionKey
      }
    }
    return success(next, [...new Set(changed)], created)
  }
  if (edit.kind === 'add-audiences') {
    if (!edit.terms.length) {
      return error('empty-targeting', 'Choose at least one segment.')
    }
    const used = campaign.variants.flatMap((variant) => audienceTerms(variant).terms)
    const terms = uniqueTerms(edit.terms).filter(
      (term) => !used.some((other) => other.groupId === term.groupId && other.segmentId === term.segmentId)
    )
    if (!terms.length) {
      return success()
    }
    const groups = edit.grouping === 'any' ? [terms] : terms.map((term) => [term])
    const variants: Variant[] = groups.map((group) => ({
      id: ids.variant(),
      name: group
        .map((term) => term.name ?? '')
        .filter(Boolean)
        .join(' or '),
      rules: {
        [edit.grouping === 'any' ? 'any' : 'all']: group.map((term) => ({
          dimensionId: term.groupId,
          segment: term.segmentId
        }))
      },
      actions: []
    }))
    return success({ ...campaign, variants: [...campaign.variants, ...variants] }, [], {
      audienceIds: variants.map((variant) => variant.id)
    })
  }
  if (edit.kind === 'order') {
    const byId = new Map(campaign.variants.map((variant) => [variant.id, variant]))
    if (new Set(edit.highestFirst).size !== edit.highestFirst.length || edit.highestFirst.some((id) => !byId.has(id))) {
      return error('invalid', 'Choose existing audiences once each.')
    }
    const order = campaign.variants.map((variant) => variant.id)
    const reordered = reorderMinimal(order, edit.highestFirst)
    if (reordered === order) {
      return success()
    }
    const variants: Variant[] = []
    for (const id of reordered) {
      const variant = byId.get(id)
      if (!variant) {
        return error('invalid', 'Choose existing audiences once each.')
      }
      variants.push(variant)
    }
    return success({ ...campaign, variants })
  }
  const versionId = 'versionId' in edit ? (edit.versionId ?? 'A') : 'A'
  const entries = campaignActions(campaign, versionId)
  const lockMessage = (id: string) =>
    id === ORIGINAL_ID
      ? 'The original page is running an A/B test. End it or restart with changes first.'
      : 'This audience is running an A/B test. End it or restart with changes first.'
  if (edit.kind === 'remove' || edit.kind === 'same-as-a' || edit.kind === 'unskip') {
    const entry =
      (edit.kind === 'remove' ? entries : campaignActions(campaign)).find((item) => item.key === edit.actionKey) ??
      // The original page has no Control changes, so its keys only exist within a variant.
      entries.find((item) => item.key === edit.actionKey && item.audience.id === ORIGINAL_ID)
    if (!entry) {
      return error('unknown-action', 'This change no longer exists.')
    }
    if (liveTestState(entry.audience, liveNode(liveCampaign, entry.audience.id)) === 'live') {
      return error('invalid', lockMessage(entry.audience.id))
    }
    // The original page's variants only add changes, so matching Control means removing the change.
    const next =
      edit.kind === 'same-as-a' && entry.audience.id !== ORIGINAL_ID
        ? clearFieldDelta(campaign, entry.audience.id, versionId, entry.action.id, edit.path)
        : edit.kind === 'unskip'
          ? unskipAction(campaign, entry.audience.id, versionId, entry.action.id)
          : removeAction(campaign, entry, versionId)
    return success(next, next === campaign ? [] : [entry.key])
  }
  if (edit.audienceId === ORIGINAL_ID) {
    if (edit.kind === 'create-test') {
      return success(createTest(campaign, ORIGINAL_ID))
    }
    if (edit.kind === 'rename' || edit.kind === 'duplicate' || edit.kind === 'delete' || edit.kind === 'targeting') {
      return error('invalid', 'The original page has no name, targeting or copies to change.')
    }
  }
  const audience =
    edit.audienceId === ORIGINAL_ID
      ? originalAudience(campaign)
      : campaign.variants.find((variant) => variant.id === edit.audienceId)
  if (!audience) {
    return error('unknown-audience', 'This audience no longer exists.')
  }
  if (
    edit.kind !== 'rename' &&
    edit.kind !== 'rename-version' &&
    edit.kind !== 'duplicate' &&
    liveTestState(audience, liveNode(liveCampaign, audience.id)) === 'live'
  ) {
    return error('invalid', lockMessage(audience.id))
  }
  if (edit.kind === 'create-test') {
    return success(createTest(campaign, audience.id))
  }
  if (edit.kind === 'add-version') {
    return success(addVersion(campaign, audience.id))
  }
  if (edit.kind === 'remove-version') {
    return success(removeVersion(campaign, audience.id, edit.versionId))
  }
  if (edit.kind === 'remove-test') {
    // Only an unstarted test disappears without a decision; a started one is ended so its history stays meaningful.
    if (audience.test?.state !== 'setup') {
      return error('invalid', "Only a test that hasn't started can be removed. End a started test instead.")
    }
    return success(endTest(campaign, audience.id, 'A'))
  }
  if (edit.kind === 'rename-version') {
    return success(renameVersion(campaign, audience.id, edit.versionId, edit.name))
  }
  if (edit.kind === 'set-weights') {
    return success(setWeights(campaign, audience.id, edit.weights))
  }
  if (edit.kind === 'delete') {
    return success(
      { ...campaign, variants: campaign.variants.filter((variant) => variant !== audience) },
      entries.filter((entry) => entry.audience === audience).map((entry) => entry.key)
    )
  }
  if (edit.kind === 'rename') {
    return audience.name === edit.name.trim()
      ? success()
      : success(replaceAudience(campaign, { ...audience, name: edit.name.trim() }))
  }
  if (edit.kind === 'duplicate') {
    const copy = {
      ...klona(audience),
      id: ids.variant(),
      name: `${edit.name ?? audience.name} copy`,
      actions: audience.actions.map((action) => ({ ...klona(action), id: ids.action() }))
    }
    delete copy.test
    const next = { ...campaign, variants: [...campaign.variants, copy] }
    return success(
      next,
      campaignActions(next)
        .filter((entry) => entry.audience === copy)
        .map((entry) => entry.key),
      { audienceIds: [copy.id] }
    )
  }
  if (edit.kind === 'targeting') {
    if (!edit.terms.length) {
      return error('empty-targeting', 'Choose at least one segment.')
    }
    const rules = { ...audience.rules }
    delete rules.all
    delete rules.any
    rules[edit.operator] = uniqueTerms(edit.terms).map((term) => ({
      dimensionId: term.groupId,
      segment: term.segmentId
    }))
    return success(replaceAudience(campaign, { ...audience, rules }))
  }
  if (versionId !== 'A' && !audience.test?.versions.some((version) => version.id === versionId)) {
    return error('invalid', `Unknown version ${versionId}.`)
  }
  const entry = edit.actionKey
    ? entries.find((item) => item.key === edit.actionKey && item.audience.id === audience.id)
    : undefined
  if (edit.actionKey && !entry) {
    return error('unknown-action', 'This change no longer exists.')
  }
  const initial =
    entry?.action ??
    withTarget(
      {
        id: '',
        $type: 'MODIFY_ELEMENT',
        page: edit.pages ?? [{ ...edit.page, operator: 'equals' }],
        modifications: {}
      },
      edit.target
    )
  if (!initial) {
    return error('invalid', 'Choose a valid element target.')
  }
  const action = patchAction(initial, edit.patch)
  if (!action) {
    return error('invalid', 'Choose a valid element target.')
  }
  if (entry && JSON.stringify(action) === JSON.stringify(entry.action)) {
    return success()
  }
  const inherited =
    versionId !== 'A' && entry ? audience.actions.find((item) => item.id === entry.action.id) : undefined
  if (entry && inherited) {
    if (edit.patch.kind === 'pages' || edit.patch.kind === 'target') {
      const skipped = skipAction(campaign, audience.id, versionId, inherited.id)
      const updatedAudience = skipped.variants.find((item) => item.id === audience.id)
      const version = updatedAudience?.test?.versions.find((item) => item.id === versionId)
      if (!updatedAudience || !version || version.id === 'A') {
        return error('invalid', `Unknown version ${versionId}.`)
      }
      const replacement = { ...action, id: ids.action() }
      const next = replaceAudience(
        skipped,
        replaceActions(updatedAudience, versionId, [...version.actions, replacement])
      )
      const key = campaignActions(next, versionId).find((item) => item.action === replacement)?.key
      return success(next, [entry.key, ...(key ? [key] : [])], { audienceIds: [], ...(key ? { actionKey: key } : {}) })
    }
    let next = campaign
    for (const path of patchPaths(edit.patch)) {
      const value = fieldValue(action, path)
      if (JSON.stringify(value) === JSON.stringify(fieldValue(entry.action, path))) {
        continue
      }
      next =
        JSON.stringify(value) === JSON.stringify(fieldValue(inherited, path))
          ? clearFieldDelta(next, audience.id, versionId, inherited.id, path)
          : setFieldDelta(
              next,
              audience.id,
              versionId,
              inherited.id,
              value === undefined ? { path, op: 'remove' } : { path, op: 'set', value: value as Json }
            )
    }
    return success(next, next === campaign ? [] : [entry.key])
  }
  if (!meaningful(action)) {
    return entry ? success(removeAction(campaign, entry, versionId), [entry.key]) : success()
  }
  if (!entry) {
    action.id = ids.action()
  }
  const version = audience.test?.versions.find((item) => item.id === versionId)
  const actions = version && version.id !== 'A' ? version.actions : audience.actions
  const updated = replaceActions(
    audience,
    versionId,
    entry
      ? actions.map((item, index) =>
          (versionId === 'A' ? index === entry.index : item.id === entry.action.id) ? action : item
        )
      : [...actions, action]
  )
  const next = replaceAudience(campaign, updated)
  const key = entry?.key ?? campaignActions(next, versionId).find((item) => item.action === action)?.key
  return success(next, key ? [key] : [], { audienceIds: [], ...(!entry && key ? { actionKey: key } : {}) })
}
